/**
 * PurchaseHistoryWidget Component
 *
 * Compact widget showing recent purchase decisions.
 * Displays decision history with filtering and quick stats.
 *
 * Features:
 * - Recent decisions list
 * - Filter by decision type
 * - Today's stats summary
 * - Expandable/collapsible design
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Collapse,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  keyframes,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CancelIcon from '@mui/icons-material/Cancel';

// Helper function to format relative time without external dependencies
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    return date.toLocaleDateString();
  }
}

// Slide in animation
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export interface DecisionHistoryItem {
  purchaseId: string;
  purchase: {
    merchantName: string;
    amount: number;
    category?: string;
  };
  decision: {
    decision: 'green' | 'yellow' | 'red';
    confidence: number;
  };
  action: 'approve' | 'delay' | 'decline';
  actionAt: Date;
}

export interface TodayStats {
  approved: number;
  delayed: number;
  declined: number;
  totalAmount: number;
}

export interface PurchaseHistoryWidgetProps {
  history: DecisionHistoryItem[];
  stats: TodayStats;
  maxItems?: number;
  onClearHistory?: () => void;
  compact?: boolean;
}

type FilterType = 'all' | 'approve' | 'delay' | 'decline';

// Action configuration
const actionConfig = {
  approve: {
    label: 'Approved',
    icon: CheckCircleIcon,
    color: '#10B981',
    bgColor: 'rgba(16, 185, 129, 0.1)',
  },
  delay: {
    label: 'Delayed',
    icon: ScheduleIcon,
    color: '#3B82F6',
    bgColor: 'rgba(59, 130, 246, 0.1)',
  },
  decline: {
    label: 'Declined',
    icon: CancelIcon,
    color: '#EF4444',
    bgColor: 'rgba(239, 68, 68, 0.1)',
  },
};

interface HistoryItemProps {
  item: DecisionHistoryItem;
  index: number;
}

function HistoryItem({ item, index }: HistoryItemProps) {
  const config = actionConfig[item.action];
  const Icon = config.icon;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        borderRadius: 2,
        backgroundColor: config.bgColor,
        animation: `${slideIn} 0.3s ease-out`,
        animationDelay: `${index * 50}ms`,
        animationFillMode: 'backwards',
      }}
    >
      {/* Action icon */}
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          boxShadow: 1,
        }}
      >
        <Icon sx={{ fontSize: 20, color: config.color }} />
      </Box>

      {/* Details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {item.purchase.merchantName}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {formatTimeAgo(new Date(item.actionAt))}
        </Typography>
      </Box>

      {/* Amount */}
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          fontFamily: 'monospace',
          color: item.action === 'approve' ? 'error.main' : 'text.secondary',
          textDecoration: item.action !== 'approve' ? 'line-through' : 'none',
        }}
      >
        ${item.purchase.amount.toFixed(2)}
      </Typography>
    </Box>
  );
}

export function PurchaseHistoryWidget({
  history,
  stats,
  maxItems = 5,
  onClearHistory,
  compact = false,
}: PurchaseHistoryWidgetProps) {
  const [expanded, setExpanded] = useState(!compact);
  const [filter, setFilter] = useState<FilterType>('all');

  // Filter history
  const filteredHistory = useMemo(() => {
    let filtered = history;
    if (filter !== 'all') {
      filtered = history.filter((item) => item.action === filter);
    }
    return filtered.slice(0, maxItems);
  }, [history, filter, maxItems]);

  // No history message
  if (history.length === 0) {
    return (
      <Card variant="outlined" sx={{ opacity: 0.7 }}>
        <CardContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body2" color="text.secondary">
            No purchase decisions yet today
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Your decisions will appear here
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: expanded ? 2 : 0,
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Today's Decisions
          </Typography>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            size="small"
            aria-label={expanded ? 'Collapse' : 'Expand'}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {/* Stats bar */}
          <Stack
            direction="row"
            spacing={2}
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'action.hover',
            }}
          >
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: actionConfig.approve.color }}
              >
                {stats.approved}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Approved
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: actionConfig.delay.color }}
              >
                {stats.delayed}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Delayed
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box sx={{ textAlign: 'center', flex: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: actionConfig.decline.color }}
              >
                {stats.declined}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Declined
              </Typography>
            </Box>
          </Stack>

          {/* Total spent */}
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              backgroundColor: stats.totalAmount > 0 ? 'rgba(239, 68, 68, 0.1)' : 'action.hover',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Total Approved Today
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontFamily: 'monospace',
                color: stats.totalAmount > 0 ? 'error.main' : 'text.secondary',
              }}
            >
              ${stats.totalAmount.toFixed(2)}
            </Typography>
          </Box>

          {/* Filter buttons */}
          <ToggleButtonGroup
            value={filter}
            exclusive
            onChange={(_, value) => value && setFilter(value)}
            size="small"
            fullWidth
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all" sx={{ flex: 1 }}>
              All
            </ToggleButton>
            <ToggleButton
              value="approve"
              sx={{ flex: 1, color: filter === 'approve' ? actionConfig.approve.color : undefined }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </ToggleButton>
            <ToggleButton
              value="delay"
              sx={{ flex: 1, color: filter === 'delay' ? actionConfig.delay.color : undefined }}
            >
              <ScheduleIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </ToggleButton>
            <ToggleButton
              value="decline"
              sx={{ flex: 1, color: filter === 'decline' ? actionConfig.decline.color : undefined }}
            >
              <CancelIcon sx={{ fontSize: 16, mr: 0.5 }} />
            </ToggleButton>
          </ToggleButtonGroup>

          {/* History list */}
          <Stack spacing={1}>
            {filteredHistory.length > 0 ? (
              filteredHistory.map((item, index) => (
                <HistoryItem key={item.purchaseId} item={item} index={index} />
              ))
            ) : (
              <Typography
                variant="body2"
                sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}
              >
                No {filter !== 'all' ? filter + 'd' : ''} decisions
              </Typography>
            )}
          </Stack>

          {/* Show more / Clear history */}
          {history.length > maxItems && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Chip
                label={`+${history.length - maxItems} more`}
                size="small"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              {onClearHistory && (
                <Chip
                  label="Clear"
                  size="small"
                  onClick={onClearHistory}
                  onDelete={onClearHistory}
                  sx={{ cursor: 'pointer' }}
                />
              )}
            </Box>
          )}
        </Collapse>

        {/* Collapsed summary */}
        {!expanded && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            {stats.approved > 0 && (
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                label={stats.approved}
                sx={{
                  backgroundColor: actionConfig.approve.bgColor,
                  color: actionConfig.approve.color,
                }}
              />
            )}
            {stats.delayed > 0 && (
              <Chip
                size="small"
                icon={<ScheduleIcon sx={{ fontSize: '14px !important' }} />}
                label={stats.delayed}
                sx={{
                  backgroundColor: actionConfig.delay.bgColor,
                  color: actionConfig.delay.color,
                }}
              />
            )}
            {stats.declined > 0 && (
              <Chip
                size="small"
                icon={<CancelIcon sx={{ fontSize: '14px !important' }} />}
                label={stats.declined}
                sx={{
                  backgroundColor: actionConfig.decline.bgColor,
                  color: actionConfig.decline.color,
                }}
              />
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

export default PurchaseHistoryWidget;
